/**
 * LoginPageLaunch — Login del SSO Launcher
 *
 * Ruta: /launch-login
 *
 * Espeja el flujo de ButtonGoogle:
 *   Google OAuth → Google userinfo → Cognito roles → Redux → navigate(/launch)
 *
 * Cuando el usuario llega a /launch, Redux ya tiene perfil + roles + countriesOptions.
 */

// libraries
import { useEffect, useState } from 'react';
import { useGoogleLogin, TokenResponse, GoogleOAuthProvider } from '@react-oauth/google';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// domain
import {
  setUser,
  setCountry,
  setRolesFromCognito,
  setCountriesOptions,
  useLazyGetUserRoleQuery,
} from '@domains/user';
import { getCountryCodeByEmail } from '@domains/country';

// assets
import { ImageHero } from '@assets/images';

// styles
import { WrapperLogin } from '../LoginPage/styles';

// components
import { LoginHeader, AutoLoginLoader } from '../LoginPage/components';

// ─────────────────────────────────────────────────────────────────────────────
// localStorage — claves exclusivas del launcher
// ─────────────────────────────────────────────────────────────────────────────
const LAUNCH_TOKEN_KEY     = 'launch_token';
const LAUNCH_GOOGLE_ID_KEY = 'launch_googleId';
const LAUNCH_PROFILE_KEY   = 'launch_profile';

const saveLaunchStorage = (
  token: string,
  googleId: string,
  email: string,
  name: string,
  lastName: string,
  imageProfile: string,
): void => {
  localStorage.setItem(LAUNCH_TOKEN_KEY, token);
  localStorage.setItem(LAUNCH_GOOGLE_ID_KEY, googleId);
  localStorage.setItem(LAUNCH_PROFILE_KEY, JSON.stringify({ email, name, lastName, imageProfile }));
};

// ─────────────────────────────────────────────────────────────────────────────
// Botón Google — solo dispara OAuth y devuelve el perfil
// ─────────────────────────────────────────────────────────────────────────────
interface GoogleProfile {
  sub: string;
  email: string;
  given_name: string;
  family_name: string;
  picture: string;
  name: string;
}

interface InternalGoogleButtonProps {
  onSuccess: (token: string, profile: GoogleProfile) => void;
  onError: () => void;
  loading: boolean;
}

const InternalGoogleButton = ({ onSuccess, onError, loading }: InternalGoogleButtonProps) => {
  const loginGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse: TokenResponse) => {
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const profile: GoogleProfile = await res.json();
        onSuccess(tokenResponse.access_token, profile);
      } catch {
        onError();
      }
    },
    onError,
  });

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" />
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px' }}>
          Autenticando...
        </span>
      </div>
    );
  }

  return (
    <button
      onClick={() => loginGoogle()}
      style={{
        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 32px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'opacity 0.2s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
    >
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      Ingresar con Google
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const LoginPageLaunchInner = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const [error, setError]     = useState(false);
  const [logging, setLogging] = useState(false);

  const [triggerCognito, {
    data: cognitoData,
    isSuccess: cognitoSuccess,
    isError: cognitoError,
  }] = useLazyGetUserRoleQuery();

  // Cuando Cognito responde → despachar roles + países → navegar
  useEffect(() => {
    if (cognitoSuccess && cognitoData) {
      dispatch(setRolesFromCognito(cognitoData.roles));
      dispatch(setCountriesOptions(cognitoData.countriesSelect));
      if (cognitoData.countriesByUser[0]) {
        dispatch(setCountry(cognitoData.countriesByUser[0]));
      }
      navigate('/launch');
    }
  }, [cognitoSuccess, cognitoData, dispatch, navigate]);

  useEffect(() => {
    if (cognitoError) {
      setLogging(false);
      setError(true);
    }
  }, [cognitoError]);

  const handleGoogleSuccess = (token: string, profile: GoogleProfile) => {
    setLogging(true);

    // 1. Despachar perfil a Redux
    dispatch(
      setUser({
        email:      profile.email,
        givenName:  profile.given_name,
        familyName: profile.family_name,
        googleId:   profile.sub,
        imageUrl:   profile.picture,
        name:       profile.name,
      }),
    );

    // 2. Guardar en localStorage para SSO hacia herramientas externas
    saveLaunchStorage(
      token,
      profile.sub,
      profile.email,
      profile.given_name,
      profile.family_name,
      profile.picture,
    );

    // 3. Derivar país del email y llamar a Cognito
    try {
      const country = getCountryCodeByEmail(profile.email);
      if (country) {
        dispatch(setCountry(country));
        triggerCognito({ country, userId: profile.sub, external: false });
      } else {
        navigate('/launch');
      }
    } catch {
      navigate('/launch');
    }
  };

  const handleError = () => {
    setLogging(false);
    setError(true);
  };

  return (
    <Container fluid className="p-0">
      <div className="d-flex">
        <div className="p-0">
          <ImageHero style={{ height: '100vh' }} />
        </div>

        <div className="d-flex align-items-center justify-content-center w-100 mb-5">
          {logging ? (
            <AutoLoginLoader
              title="Preparando launcher..."
              subtitle="Validando roles y permisos"
            />
          ) : (
            <WrapperLogin className="mb-5">
              <div className="d-flex flex-column align-items-center gap-3 mb-5">
                <LoginHeader subtitle="SSO Launcher" />
                <div className="google">
                  Inicia sesión para acceder <br /> a los módulos de Polynator
                </div>

                <InternalGoogleButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleError}
                  loading={logging}
                />

                {error && (
                  <div
                    style={{
                      background: '#fef2f2',
                      color: '#dc2626',
                      border: '1px solid #fca5a5',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  >
                    ❌ Error al iniciar sesión. Intenta de nuevo.
                  </div>
                )}
              </div>
            </WrapperLogin>
          )}
        </div>
      </div>
    </Container>
  );
};

export const LoginPageLaunch = () => {
  const authKey = process.env.ENV_GOOGLE_AUTH;

  if (!authKey) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-danger text-center">
          <strong>Error:</strong> No se encontró ENV_GOOGLE_AUTH en las variables de entorno.
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={authKey}>
      <LoginPageLaunchInner />
    </GoogleOAuthProvider>
  );
};
